import { Skeleton, ErrorBoundary, Stack, VStack } from '@genial/components';
import { BoxRadius, Color, Spacing, Typography } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import { Box } from 'native-base';
import React, { useCallback, useMemo, useState } from 'react';
import {
  Bar,
  Cell,
  ComposedChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { useRecoilQuery } from '../../../../../../hooks/useRecoilQuery';
import {
  financialAnalysisAverageTicketBigNumbersQuery,
  financialAnalysisAverageTicketChartQuery,
} from '../../../../../../store/home/capture/selector';
import { chartNameMap } from '../../constants';
import { FinancialAnalysisComponentTypes } from '../../types';
import { AverageTicketBigNumberCard } from '../AverageTicketBigNumberCard';
import { AverageTicketChartTooltip } from '../AverageTicketChartTooltip';
import { AverageTicketChartYTick } from '../AverageTicketChartYTick';

const axisDefaultConfig = {
  fontFamily: Typography['fontfamily.base.regular'],
  fontSize: Typography['fontsize.sm'],
  tickLine: false,
  axisLine: false,
};

export const AverageTicket: React.FC<FinancialAnalysisComponentTypes.Props> = ({ isMobile }) => {
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>(undefined);
  const {
    data: bigNumberData,
    loading: bigNumberLoading,
    error: bigNumberError,
    refresher: bigNumberRefresher,
  } = useRecoilQuery(financialAnalysisAverageTicketBigNumbersQuery);
  const {
    data: chartData,
    loading: chartLoading,
    error: chartError,
    refresher: chartRefresher,
  } = useRecoilQuery(financialAnalysisAverageTicketChartQuery);

  const handleSelectIndex = useCallback(
    (index: number) => {
      if (index === selectedIndex) {
        setSelectedIndex(undefined);
        return;
      }

      setSelectedIndex(index);
    },
    [selectedIndex]
  );

  const chartContent = useMemo(() => {
    if (chartLoading) {
      return <Skeleton height={237} width="100%" borderRadius="sm" />;
    }

    if (chartError) {
      return <ErrorBoundary onRefresh={chartRefresher} />;
    }

    const maxBarSize = isMobile ? 16 : 33;
    const barRadius: [number, number, number, number] = [
      Utils.Token.ToInt(BoxRadius, 'xs'),
      Utils.Token.ToInt(BoxRadius, 'xs'),
      0,
      0,
    ];

    return (
      <ResponsiveContainer width="100%" height={237}>
        <ComposedChart data={chartData || []}>
          <XAxis
            dataKey="currentDateNormalized"
            {...axisDefaultConfig}
            tickFormatter={(_value, index) =>
              (isMobile
                ? chartData?.[index].currentDateSmallNormalized
                : chartData?.[index].currentDateNormalized) ?? ''
            }
          />
          <YAxis {...axisDefaultConfig} hide={isMobile} tick={<AverageTicketChartYTick />} />
          <ReferenceLine
            y={0}
            stroke={Utils.ColorToken.WithOpacity('brand.tertiary.lightest', 0.5)}
          />
          <Tooltip
            content={({ active, label, payload }) => (
              <AverageTicketChartTooltip
                label={label}
                active={active}
                payload={payload}
                nameMap={chartNameMap}
              />
            )}
          />
          <Bar
            type="monotone"
            dataKey="ticketAverageValue"
            stackId="ticketAverageValueDataStack"
            maxBarSize={maxBarSize}
            radius={barRadius}
            fill={Color['brand.tertiary.principal']}
            onClick={(_, index) => handleSelectIndex(index)}>
            {chartData?.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={Color['brand.tertiary.principal']}
                fillOpacity={selectedIndex === undefined || selectedIndex === index ? 1 : 0.6}
              />
            ))}
          </Bar>
          <Bar
            type="monotone"
            dataKey="ticketAverageValueStack"
            fill={Color['brand.tertiary.lightest']}
            stackId="ticketAverageValueDataStack"
            maxBarSize={maxBarSize}
            radius={barRadius}
            onClick={(_, index) => handleSelectIndex(index)}>
            {chartData?.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={Color['brand.tertiary.lightest']}
                fillOpacity={selectedIndex === undefined || selectedIndex === index ? 1 : 0.6}
              />
            ))}
          </Bar>
        </ComposedChart>
      </ResponsiveContainer>
    );
  }, [
    chartData,
    chartError,
    chartLoading,
    chartRefresher,
    handleSelectIndex,
    isMobile,
    selectedIndex,
  ]);

  return (
    <VStack space={Spacing['stack.xxxs']} flexDirection={isMobile ? 'column-reverse' : 'column'}>
      <Stack
        direction={{ base: 'column', sm: 'row' }}
        justifyContent={{ base: 'inherit', sm: 'space-between' }}
        space={Spacing['stack.xxxs']}>
        <AverageTicketBigNumberCard
          title="YTD"
          value={bigNumberData?.yearToDateValue}
          assetName="YoY"
          assetValue={bigNumberData?.previousYearValue}
          assetPercentage={bigNumberData?.yearOverYearPercentage}
          loading={bigNumberLoading}
          error={bigNumberError}
          refresher={bigNumberRefresher}
        />
        <AverageTicketBigNumberCard
          title="QTD"
          value={bigNumberData?.quarterToDateValue}
          assetName="QoQ"
          assetValue={bigNumberData?.previousQuarterValue}
          assetPercentage={bigNumberData?.quarterOverQuarterPercentage}
          loading={bigNumberLoading}
          error={bigNumberError}
          refresher={bigNumberRefresher}
        />
        <AverageTicketBigNumberCard
          title="MTD"
          value={bigNumberData?.monthToDateValue}
          assetName="MoM"
          assetValue={bigNumberData?.previousMonthValue}
          assetPercentage={bigNumberData?.monthOverMonthPercentage}
          loading={bigNumberLoading}
          error={bigNumberError}
          refresher={bigNumberRefresher}
        />
        <AverageTicketBigNumberCard
          title="WTD"
          value={bigNumberData?.weekToDateValue}
          assetName="WoW"
          assetValue={bigNumberData?.previousWeekValue}
          assetPercentage={bigNumberData?.weekOverWeekPercentage}
          loading={bigNumberLoading}
          error={bigNumberError}
          refresher={bigNumberRefresher}
        />
      </Stack>
      <Box>{chartContent}</Box>
    </VStack>
  );
};
